import { Sun } from "@faire/web/slate/icons/faire-markets/Sun";
import { Account } from "@faire/web/slate/icons/foundation/Account";
import { Add } from "@faire/web/slate/icons/foundation/Add";
import { AddUser } from "@faire/web/slate/icons/foundation/AddUser";
import { AlarmClock } from "@faire/web/slate/icons/foundation/AlarmClock";
import {
  Announcement,
  AnnouncementMuted,
} from "@faire/web/slate/icons/foundation/Announcement";
import { Application } from "@faire/web/slate/icons/foundation/Application";
import { Arrow } from "@faire/web/slate/icons/foundation/Arrow";
import { Block } from "@faire/web/slate/icons/foundation/Block";
import { BookmarkTag } from "@faire/web/slate/icons/foundation/BookmarkTag";
import { Calendar } from "@faire/web/slate/icons/foundation/Calendar";
import { CartEmpty } from "@faire/web/slate/icons/foundation/CartEmpty";
import { CartItems } from "@faire/web/slate/icons/foundation/CartItems";
import { Caution } from "@faire/web/slate/icons/foundation/Caution";
import { ChatBubble } from "@faire/web/slate/icons/foundation/ChatBubble";
import { CheckInCircle } from "@faire/web/slate/icons/foundation/CheckInCircle";
import { CheckOutline } from "@faire/web/slate/icons/foundation/CheckOutline";
import { Chevron } from "@faire/web/slate/icons/foundation/Chevron";
import { CircleFilled } from "@faire/web/slate/icons/foundation/CircleFilled";
import { Clock } from "@faire/web/slate/icons/foundation/Clock";
import { Close } from "@faire/web/slate/icons/foundation/Close";
import { Clover } from "@faire/web/slate/icons/foundation/Clover";
import { CustomerSupport } from "@faire/web/slate/icons/foundation/CustomerSupport";
import { Diamond } from "@faire/web/slate/icons/foundation/Diamond";
import { Earn } from "@faire/web/slate/icons/foundation/Earn";
import { Edit } from "@faire/web/slate/icons/foundation/Edit";
import { Email } from "@faire/web/slate/icons/foundation/Email";
import { EstimatedDeliveryDate } from "@faire/web/slate/icons/foundation/EstimatedDeliveryDate";
import { Exclamation } from "@faire/web/slate/icons/foundation/Exclamation";
import { ExclamationSolid } from "@faire/web/slate/icons/foundation/ExclamationSolid";
import { Exclusivity } from "@faire/web/slate/icons/foundation/Exclusivity";
import { Expand } from "@faire/web/slate/icons/foundation/Expand";
import { Eye, EyeClosed } from "@faire/web/slate/icons/foundation/Eye";
import { Filter } from "@faire/web/slate/icons/foundation/Filter";
import { Globe } from "@faire/web/slate/icons/foundation/Globe";
import { Group } from "@faire/web/slate/icons/foundation/Group";
import { Heart } from "@faire/web/slate/icons/foundation/Heart";
import { Information } from "@faire/web/slate/icons/foundation/Information";
import { Link } from "@faire/web/slate/icons/foundation/Link";
import { Lock } from "@faire/web/slate/icons/foundation/Lock";
import { Minimize } from "@faire/web/slate/icons/foundation/Minimize";
import { NotificationBell } from "@faire/web/slate/icons/foundation/NotificationBell";
import { NotificationBellChecked } from "@faire/web/slate/icons/foundation/NotificationBellChecked";
import { PartyPopper } from "@faire/web/slate/icons/foundation/PartyPopper";
import { PaymentPlan } from "@faire/web/slate/icons/foundation/PaymentPlan";
import { Pin } from "@faire/web/slate/icons/foundation/Pin";
import { Relationships } from "@faire/web/slate/icons/foundation/Relationships";
import { ResponsibleConsumption } from "@faire/web/slate/icons/foundation/ResponsibleConsumption";
import { Returns } from "@faire/web/slate/icons/foundation/Returns";
import { Save } from "@faire/web/slate/icons/foundation/Save";
import { Search } from "@faire/web/slate/icons/foundation/Search";
import { Send } from "@faire/web/slate/icons/foundation/Send";
import { Share } from "@faire/web/slate/icons/foundation/Share";
import { ShareHeart } from "@faire/web/slate/icons/foundation/ShareHeart";
import { ShipmentDelivered } from "@faire/web/slate/icons/foundation/ShipmentDelivered";
import { Shipping } from "@faire/web/slate/icons/foundation/Shipping";
import { ShipsFrom } from "@faire/web/slate/icons/foundation/ShipsFrom";
import { ShopifyColored } from "@faire/web/slate/icons/foundation/ShopifyColored";
import { ShoppingBag } from "@faire/web/slate/icons/foundation/ShoppingBag";
import { Sort } from "@faire/web/slate/icons/foundation/Sort";
import { Sprout } from "@faire/web/slate/icons/foundation/Sprout";
import { Square } from "@faire/web/slate/icons/foundation/Square";
import { Star } from "@faire/web/slate/icons/foundation/Star";
import { StarFilled } from "@faire/web/slate/icons/foundation/StarFilled";
import { Synchronized } from "@faire/web/slate/icons/foundation/Synchronized";
import { SynchronizedWarning } from "@faire/web/slate/icons/foundation/SynchronizedWarning";
import { TrashCan } from "@faire/web/slate/icons/foundation/TrashCan";
import { Truck } from "@faire/web/slate/icons/foundation/Truck";
import { Twinkle } from "@faire/web/slate/icons/foundation/Twinkle";
import { Undo } from "@faire/web/slate/icons/foundation/Undo";
import { VerticalEllipsis } from "@faire/web/slate/icons/foundation/VerticalEllipsis";
import { Insider } from "@faire/web/slate/icons/InsiderIcon";
import { SVGIcon } from "@faire/web/slate/icons/SVGIcon";
import { TrophyIconSecondary } from "@faire/web/slate/icons/Trophy";
import { IFaireIcon } from "@faire/web-api/indigofair/data/IFaireIcon";
import React from "react";

const DEFAULT_SLATE_ICON: React.FC<SVGIcon.IProps> = () => {
  return <div />;
};

export const getSlateIcon = (
  icon: IFaireIcon | undefined
): React.FC<SVGIcon.IProps> => {
  if (icon === undefined || icon.type === undefined) {
    return DEFAULT_SLATE_ICON;
  }
  const iconFromMap = iconTokentoSlateIconMap[icon.type];
  if (iconFromMap) {
    return iconFromMap;
  }
  // eslint-disable-next-line no-console
  console.warn(
    `Slate Icon ${icon.type} is not defined on getSlateIcon, please add it to the map on SlateMappings/getSlateIcon`
  );
  return DEFAULT_SLATE_ICON;
};

const iconTokentoSlateIconMap: Partial<
  Record<IFaireIcon.Type, React.FC<SVGIcon.IProps>>
> = {
  [IFaireIcon.Type.TYPE_UNKNOWN]: DEFAULT_SLATE_ICON,
  // TODO: Handle arrows of different directions
  [IFaireIcon.Type.ARROW]: (props: SVGIcon.IProps) => {
    return <Arrow {...props} direction="right" />;
  },
  [IFaireIcon.Type.ARROW_TOP]: (props: SVGIcon.IProps) => {
    return <Arrow {...props} direction="up" />;
  },
  [IFaireIcon.Type.ADD_USER]: AddUser,
  [IFaireIcon.Type.INSIDER_KEY]: (props: SVGIcon.IProps) => {
    return <Insider {...props} small />;
  },
  [IFaireIcon.Type.ACCOUNT]: Account,
  [IFaireIcon.Type.ADD]: Add,
  [IFaireIcon.Type.ALARM_CLOCK]: AlarmClock,
  [IFaireIcon.Type.ANNOUNCEMENT]: Announcement,
  [IFaireIcon.Type.ANNOUNCEMENT_MUTED]: AnnouncementMuted,
  [IFaireIcon.Type.APPLICATION]: Application,
  [IFaireIcon.Type.BLOCK]: Block,
  [IFaireIcon.Type.BOOKMARK_TAG]: BookmarkTag,
  [IFaireIcon.Type.CALENDAR]: Calendar,
  [IFaireIcon.Type.CART_EMPTY]: CartEmpty,
  [IFaireIcon.Type.CART_ITEMS]: CartItems,
  [IFaireIcon.Type.CAUTION]: Caution,
  [IFaireIcon.Type.CHAT_BUBBLE]: ChatBubble,
  [IFaireIcon.Type.CIRCLE_FILLED]: CircleFilled,
  [IFaireIcon.Type.CHECK_IN_CIRCLE]: CheckInCircle,
  [IFaireIcon.Type.CHECK_OUTLINE]: CheckOutline,
  // TODO: Handle chevrons of different directions
  [IFaireIcon.Type.CHEVRON]: (props: SVGIcon.IProps) => {
    return <Chevron {...props} direction="right" />;
  },
  [IFaireIcon.Type.CLOCK]: Clock,
  [IFaireIcon.Type.CLOSE]: Close,
  [IFaireIcon.Type.CLOVER]: Clover,
  [IFaireIcon.Type.CUSTOMER_SUPPORT]: CustomerSupport,
  [IFaireIcon.Type.DIAMOND]: Diamond,
  [IFaireIcon.Type.EARN]: Earn,
  [IFaireIcon.Type.EDIT]: Edit,
  [IFaireIcon.Type.EMAIL]: Email,
  [IFaireIcon.Type.ESTIMATED_DELIVERY_DATE]: EstimatedDeliveryDate,
  [IFaireIcon.Type.EXCLAMATION]: Exclamation,
  [IFaireIcon.Type.EXCLAMATION_SOLID]: ExclamationSolid,
  [IFaireIcon.Type.EXCLUSIVITY]: Exclusivity,
  [IFaireIcon.Type.EXPAND]: Expand,
  [IFaireIcon.Type.EYE]: Eye,
  [IFaireIcon.Type.EYE_CLOSED]: EyeClosed,
  [IFaireIcon.Type.FILTER]: Filter,
  [IFaireIcon.Type.GLOBE]: Globe,
  [IFaireIcon.Type.GROUP]: Group,
  [IFaireIcon.Type.HEART]: Heart,
  [IFaireIcon.Type.INFORMATION]: Information,
  [IFaireIcon.Type.LINK]: Link,
  [IFaireIcon.Type.LOCK]: (props: SVGIcon.IProps) => {
    return <Lock {...props} lockType="foundation" />;
  },
  [IFaireIcon.Type.MINIMIZE]: Minimize,
  [IFaireIcon.Type.PARTY_POPPER]: PartyPopper,
  [IFaireIcon.Type.PAYMENT_PLAN]: PaymentPlan,
  [IFaireIcon.Type.PIN]: Pin,
  [IFaireIcon.Type.RELATIONSHIPS]: Relationships,
  [IFaireIcon.Type.RESPONSIBLE_CONSUMPTION_AND_PRODUCTION]:
    ResponsibleConsumption,
  [IFaireIcon.Type.RETURNS]: Returns,
  [IFaireIcon.Type.SAVE]: Save,
  [IFaireIcon.Type.SEARCH]: Search,
  [IFaireIcon.Type.SEND]: Send,
  [IFaireIcon.Type.SHARE]: Share,
  [IFaireIcon.Type.SHARE_HEART]: ShareHeart,
  [IFaireIcon.Type.SHIPMENT_DELIVERED]: ShipmentDelivered,
  [IFaireIcon.Type.SHIPPING]: Shipping,
  [IFaireIcon.Type.SHIPS_FROM]: ShipsFrom,
  [IFaireIcon.Type.SHOPIFY_COLORED]: ShopifyColored,
  [IFaireIcon.Type.SHOPPING_BAG]: ShoppingBag,
  [IFaireIcon.Type.SORT]: Sort,
  [IFaireIcon.Type.SPROUT]: Sprout,
  [IFaireIcon.Type.SQUARE]: Square,
  [IFaireIcon.Type.STAR]: Star,
  [IFaireIcon.Type.STAR_FILLED]: StarFilled,
  [IFaireIcon.Type.SYNCHRONIZED]: Synchronized,
  [IFaireIcon.Type.SYNCHRONIZED_WARNING]: SynchronizedWarning,
  [IFaireIcon.Type.TRASH_CAN]: TrashCan,
  [IFaireIcon.Type.TRUCK]: Truck,
  [IFaireIcon.Type.TWINKLE]: Twinkle,
  [IFaireIcon.Type.UNDO]: Undo,
  [IFaireIcon.Type.VERTICAL_ELLIPSIS]: VerticalEllipsis,
  [IFaireIcon.Type.TROPHY_ICON_SECONDARY]: TrophyIconSecondary,
  [IFaireIcon.Type.NOTIFICATION_BELL_CHECKED]: NotificationBellChecked,
  [IFaireIcon.Type.NOTIFICATION_BELL]: NotificationBell,
  [IFaireIcon.Type.FAIRE_SUMMER_MARKET]: (props: SVGIcon.IProps) => {
    return <Sun {...props} />;
  },
};
